'use client'

import Link from 'next/link'
import { useEffect, useState } from 'react'

import { FamilyOfBrands } from '@/app/components/FamilyOfBrands'
import { Navigation } from '@/app/components/Navigation'
import SeparatorSection from '@/app/components/PageBuilder/SeparatorSection'
import { PavedLogo } from '@/app/components/PavedLogo'
import { Social } from '@/app/components/Social'
import { TRACKING_LOCATIONS } from '@/app/utils/analytics'
import { cn } from '@/app/utils/style'
import { NavQueryResult, StructuredDataQueryResult } from '@/sanity.types'

interface FooterProps {
  className?: string
  showTopDecor?: boolean
  navigations: Array<NavQueryResult[number] | null>
  socialProfiles?: NonNullable<
    NonNullable<StructuredDataQueryResult>['organizationShared']
  >['socialProfiles']
  websiteDescription?: NonNullable<StructuredDataQueryResult>['websiteDescription']
}

export const Footer = function Footer({
  className,
  showTopDecor = false,
  navigations: initialNavigations,
  socialProfiles,
  websiteDescription,
}: FooterProps) {
  const [navigations, setNavigations] = useState(initialNavigations)
  const footerLegalNav = navigations.find((nav) => nav?.location === 'footer_legal')

  useEffect(() => {
    setNavigations(initialNavigations)
  }, [initialNavigations])

  return (
    <>
      {showTopDecor && (
        <SeparatorSection block={{ _type: 'separatorSection', mode: 'whiteToGreen' }} index={0} />
      )}
      <footer className={cn('relative py-9xl px-8xl lg:px-10xl bg-green-700', className)}>
        <div className="relative z-10 max-w-[1280px] mx-auto">
          <div className="flex flex-col gap-5xl lg:flex-row lg:justify-between">
            <div className="flex flex-col gap-lg">
              <Link className="w-fit md:shrink-0" href="/" aria-label="Paved">
                <PavedLogo className="w-[100px] h-[36px] text-green-100" />
              </Link>

              {websiteDescription && (
                <p className="size-body-sm text-green-50 max-w-[240px]">{websiteDescription}</p>
              )}
            </div>

            {navigations && (
              <div className="grid grid-col md:grid-cols-2 gap-6xl lg:flex lg:flex-row lg:gap-9xl xl:gap-12xl">
                {navigations
                  .filter((nav) => nav?.location !== 'footer_legal')
                  .map((nav) => (
                    <Navigation
                      key={nav?._id || nav?.title}
                      classNames={{
                        root: 'flex flex-col gap-lg',
                        label: 'size-header-h6 text-green-50 pl-sm',
                        list: 'flex flex-col gap-lg',
                        item: '',
                        link: 'box-decoration-clone size-body-xs py-xs px-sm text-white-default rounded-5xl hover:bg-green-500 hover:text-white-default active:bg-green-100 active:text-white-950 hover:active:text-white-950 hover:active:bg-green-100 focus:outline-none transition-all duration-200',
                      }}
                      label={nav?.title}
                      nav={nav}
                      tracking={{ location: TRACKING_LOCATIONS.FOOTER }}
                    />
                  ))}
              </div>
            )}
          </div>
          <div className="flex flex-col gap-6xl pt-xl border-t border-t-neon-200/50 mt-10xl md:flex-row md:justify-between md:items-center">
            <p className="size-body-xs text-green-50 text-center" suppressHydrationWarning>
              © {new Date().getFullYear()} Paved. All rights reserved.
            </p>
            <div className="flex flex-col gap-xl items-center md:flex-row">
              {footerLegalNav && (
                <Navigation
                  key={footerLegalNav._id || footerLegalNav.title}
                  classNames={{
                    root: 'flex flex-col gap-lg',
                    label: 'size-header-h6 text-green-50 pl-sm',
                    list: 'flex flex-col items-center sm:items-start sm:flex-row gap-lg [&_a[aria-label*="Privacy_Choices"]]:flex [&_a[aria-label*="Privacy_Choices"]]:items-center [&_a[aria-label*="Privacy_Choices"]::before]:content-[""] [&_a[aria-label*="Privacy_Choices"]::before]:inline-block [&_a[aria-label*="Privacy_Choices"]::before]:w-[32px] [&_a[aria-label*="Privacy_Choices"]::before]:h-[20px] [&_a[aria-label*="Privacy_Choices"]::before]:mr-xs [&_a[aria-label*="Privacy_Choices"]::before]:bg-[url(/images_pwf/privacy_choices.svg)] [&_a[aria-label*="Privacy_Choices"]::before]:bg-contain [&_a[aria-label*="Privacy_Choices"]::before]:bg-center [&_a[aria-label*="Privacy_Choices"]::before]:bg-no-repeat',
                    item: '',
                    link: 'box-decoration-clone size-body-xs py-xs px-sm text-white-default rounded-5xl hover:bg-green-500 hover:text-white-default active:bg-green-100 active:text-white-950 hover:active:text-white-950 hover:active:bg-green-100 focus:outline-none transition-all duration-200',
                  }}
                  nav={footerLegalNav}
                  tracking={{ location: TRACKING_LOCATIONS.FOOTER }}
                />
              )}
              <Social
                classNames={{
                  root: 'gap-lg justify-center',
                  link: 'w-6 h-6 flex items-center justify-center text-white-default hover:text-neon-100 active:text-neon-200',
                }}
                iconSize={16}
                profiles={socialProfiles}
                order={['twitter', 'linkedin', 'instagram', 'facebook']}
              />
            </div>
          </div>
        </div>
        <div className="relative z-10 max-w-[1280px] mx-auto">
          <FamilyOfBrands
            classNames={{
              root: 'flex flex-col gap-6xl mt-9xl',
              lead: 'size-body-xs text-neutral-50 text-center',
              container: 'flex flex-wrap justify-center items-center gap-8xl',
            }}
          />
        </div>
      </footer>
    </>
  )
}
